import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["onKeydown"]
const _hoisted_2 = { class: "consultation-invite__icon-wrap" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "permission-request-drawer__close-trigger" }
const _hoisted_5 = { class: "permission-request-drawer__section" }
const _hoisted_6 = { class: "permission-request-drawer__section-content-container" }
const _hoisted_7 = { class: "permission-request-drawer__row" }
const _hoisted_8 = { class: "permission-request-drawer__row" }
const _hoisted_9 = { class: "permission-request-drawer__multiselect-option" }
const _hoisted_10 = { class: "permission-request-drawer__multiselect-option-label" }
const _hoisted_11 = { class: "permission-request-drawer__checkbox-group-row" }
const _hoisted_12 = { class: "permission-request-drawer__section" }
const _hoisted_13 = { class: "permission-request-drawer__section-content-container" }
const _hoisted_14 = { class: "permission-request-drawer__row" }
const _hoisted_15 = { class: "permission-request-drawer__section" }
const _hoisted_16 = { class: "permission-request-drawer__section-content-container" }
const _hoisted_17 = { class: "permission-request-drawer__footer" }
const _hoisted_18 = { class: "permission-request-drawer__buttons-container" }
const _hoisted_19 = ["disabled"]

import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue';

import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import ruRu from 'ant-design-vue/es/date-picker/locale/ru_RU';
import { type FormSubjectOption } from '@/js/components/form/form-subject-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import FormPhoneField from '@/js/components/form/form-phone-field.vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const EDUCATION_PERMISSION_OPEN_EVENT = 'stc:education-permission-open';
const EDUCATION_PROGRAM_DESCRIPTION_MAX_LENGTH = 512;

interface EducationFormMetaItem {
  id: string;
  text: string;
}

interface EducationFormMetaResponse {
  status?: string;
  msg?: {
    education_types?: EducationFormMetaItem[];
    implementation_form?: EducationFormMetaItem[];
    privacy?: EducationFormMetaItem[];
  };
  error?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'permission-request',
  props: {
    launcher: { default: 'button' }
  },
  setup(__props: any) {

const props = __props;

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const pickerLocale = ruRu;
dayjs.locale('ru');
const EDUCATION_FORM_ENDPOINT = getApiUrl('/api/form/education/');

function mapMetaToOptions(items: EducationFormMetaItem[] | undefined): FormSubjectOption[] {
  return (items ?? []).map((item) => ({ value: item.id, label: item.text }));
}

const educationTypesAndLevelsOptions = ref<FormSubjectOption[]>([]);
const educationActivityFormatOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref('');

const openModal = ref<boolean>(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);
const generalFormRef = ref<FormInstance>();
const contactFormRef = ref<FormInstance>();
const documentsUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const drawerContentRef = ref<HTMLElement | null>(null);
const documentItems = ref<FileUploadItem[]>([]);
const open = ref<boolean>(false);
const drawerRenderKey = ref(0);

const initialFormState = () => ({
  fullLegalName: '',
  shortLegalName: '',
  organizationalLegalForm: '',
  locationAddress: '',
  ogrnOrOgrnip: '',
  registrationDate: null as Dayjs | null,
  registrationAuthority: '',
  educationTypesAndLevels: [] as string[],
  educationActivityFormats: [] as string[],
  contactFullName: '',
  phone: '',
  contactEmail: '',
  educationProgramDescription: '',
  privacy: false,
});

const form = reactive(initialFormState());

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.fullLegalName) &&
    isNotBlankString(form.shortLegalName) &&
    isNotBlankString(form.organizationalLegalForm) &&
    isNotBlankString(form.locationAddress) &&
    isNotBlankString(form.ogrnOrOgrnip) &&
    form.registrationDate !== null &&
    isNotBlankString(form.registrationAuthority) &&
    form.educationTypesAndLevels.length > 0 &&
    form.educationActivityFormats.length > 0 &&
    isNotBlankString(form.contactFullName) &&
    isNotBlankString(form.phone) &&
    isNotBlankString(form.contactEmail) &&
    isNotBlankString(form.educationProgramDescription) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.contactEmail));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  fullLegalName: [{ required: true, whitespace: true, message: 'Введите полное название' }],
  shortLegalName: [{ required: true, whitespace: true, message: 'Введите сокращенное название' }],
  organizationalLegalForm: [
    { required: true, whitespace: true, message: 'Введите организационно-правовую форму' },
  ],
  locationAddress: [{ required: true, whitespace: true, message: 'Введите адрес местонахождения' }],
  ogrnOrOgrnip: [{ required: true, whitespace: true, message: 'Введите ОГРН/ОГРНИП' }],
  registrationDate: [{ required: true, message: 'Выберите дату регистрации' }],
  registrationAuthority: [
    { required: true, whitespace: true, message: 'Введите наименование органа' },
  ],
  educationTypesAndLevels: [
    { required: true, type: 'array', min: 1, message: 'Выберите хотя бы один вариант' },
  ],
  educationActivityFormats: [
    { required: true, type: 'array', min: 1, message: 'Выберите хотя бы одну форму' },
  ],
  contactFullName: [{ required: true, whitespace: true, message: 'Введите ФИО' }],
  phone: [{ required: true, whitespace: true, message: 'Введите телефон' }],
  contactEmail: [{ required: true, type: 'email', message: 'Введите корректный e-mail' }],
  educationProgramDescription: [
    { required: true, whitespace: true, message: 'Заполните описание программы' },
  ],
};

const showModal = () => {
  openModal.value = true;
};

const showDrawer = () => {
  resetFormState();
  drawerRenderKey.value += 1;
  open.value = true;
};

const onEducationPermissionOpen = () => {
  showDrawer();
};

const fetchEducationFormMeta = async () => {
  try {
    const response = await fetch(EDUCATION_FORM_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
    });

    const result = (await response.json()) as EducationFormMetaResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(result.error ?? 'Не удалось загрузить данные формы');
    }

    const privacyList = result.msg?.privacy ?? [];

    educationTypesAndLevelsOptions.value = mapMetaToOptions(result.msg?.education_types);
    educationActivityFormatOptions.value = mapMetaToOptions(result.msg?.implementation_form);
    privacyOptionId.value = privacyList[0]?.id ?? '';
  } catch {
    message.error('Не удалось загрузить данные формы');
  }
};

onMounted(() => {
  void fetchEducationFormMeta();
  window.addEventListener(EDUCATION_PERMISSION_OPEN_EVENT, onEducationPermissionOpen);
});

onUnmounted(() => {
  window.removeEventListener(EDUCATION_PERMISSION_OPEN_EVENT, onEducationPermissionOpen);
});

const resetFormState = () => {
  Object.assign(form, initialFormState());
  documentItems.value = [];
  documentsUploadRef.value?.reset();
  nextTick(() => {
    generalFormRef.value?.clearValidate();
    contactFormRef.value?.clearValidate();
  });
};

const scrollDrawerToTop = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const drawerBody = drawerContentRef.value?.closest('.ant-drawer-body');
      drawerBody?.scrollTo({ top: 0, left: 0 });
    });
  });
};

const onAfterOpenChange = (isOpen: boolean) => {
  if (isOpen) {
    scrollDrawerToTop();
  }
};

const onClose = () => {
  resetFormState();
  open.value = false;
};

const parseContactNameParts = (fullName: string): [string, string, string] => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const lastName = parts[0] ?? '';
  const firstName = parts[1] ?? '';
  const middleName = parts.slice(2).join(' ');
  return [lastName, firstName, middleName];
};

const onSubmit = async () => {
  if (!generalFormRef.value || !contactFormRef.value) {
    return;
  }

  try {
    await Promise.all([generalFormRef.value.validate(), contactFormRef.value.validate()]);
  } catch {
    return;
  }

  if (form.privacy && !privacyOptionId.value) {
    message.error('Не удалось определить согласие на обработку данных');
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const readyFiles = documentsUploadRef.value?.getReadyFiles() ?? [];

    const submitResult = await submitFormWithCaptcha({
      endpoint: EDUCATION_FORM_ENDPOINT,
      buildFormData: () => {
        const body = new FormData();
        const [contactLastName, contactFirstName, contactMiddleName] = parseContactNameParts(
          form.contactFullName,
        );

        body.append('full_name', form.fullLegalName);
        body.append('short_name', form.shortLegalName);
        body.append('legal_form', form.organizationalLegalForm);
        body.append('address', form.locationAddress);
        body.append('ogrn', form.ogrnOrOgrnip);
        if (form.registrationDate) {
          body.append('registration_date', form.registrationDate.format('YYYY-MM-DD'));
        }
        body.append('registration_authority', form.registrationAuthority);

        form.educationTypesAndLevels.forEach((value) => {
          body.append('education_types[]', value);
        });
        form.educationActivityFormats.forEach((value) => {
          body.append('implementation_form[]', value);
        });

        body.append('last_name', contactLastName);
        body.append('first_name', contactFirstName);
        body.append('middle_name', contactMiddleName);
        body.append('email', form.contactEmail);
        body.append('phone', form.phone);
        body.append('program_description', form.educationProgramDescription);
        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        readyFiles.forEach((file) => {
          body.append('documents[]', file);
        });

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить заявку');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    trackYaGoal('submitApplication');
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
    documentsUploadRef.value?.resetSubmittingState();
  }
};

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_date_picker = _resolveComponent("a-date-picker")!
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_checkbox = _resolveComponent("a-checkbox")!
  const _component_a_checkbox_group = _resolveComponent("a-checkbox-group")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.launcher === 'float')
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "consultation-invite__launcher",
          role: "button",
          tabindex: "0",
          "aria-label": "Учебная деятельность — оставить заявку",
          onClick: showDrawer,
          onKeydown: [
            _withKeys(_withModifiers(showDrawer, ["prevent"]), ["enter"]),
            _withKeys(_withModifiers(showDrawer, ["prevent"]), ["space"])
          ]
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("img", {
              src: `${_unref(ICONS_BASE)}/book-education.svg`,
              alt: "",
              class: "consultation-invite__icon consultation-invite__icon--book"
            }, null, 8, _hoisted_3)
          ]),
          _cache[17] || (_cache[17] = _createElementVNode("div", { class: "consultation-invite__lead" }, [
            _createElementVNode("p", { class: "consultation-invite__title" }, "Образовательная деятельность"),
            _createElementVNode("p", { class: "consultation-invite__subtitle" }, "Оставьте заявку")
          ], -1))
        ], 40, _hoisted_1))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          onClick: showDrawer
        }, [...(_cache[18] || (_cache[18] = [
          _createElementVNode("div", { class: "btn btn-primary" }, "Подать заявку", -1)
        ]))])),
    (_openBlock(), _createBlock(_component_a_drawer, {
      key: drawerRenderKey.value,
      open: open.value,
      class: "permission-request-drawer",
      "root-class-name": "permission-request-drawer",
      "destroy-on-close": true,
      onAfterOpenChange: onAfterOpenChange,
      onClose: onClose
    }, {
      title: _withCtx(() => [...(_cache[19] || (_cache[19] = [
        _createElementVNode("div", { class: "permission-request-drawer__header-container" }, [
          _createElementVNode("span", { class: "permission-request-drawer__header-container--title" }, "Заявка на разрешение"),
          _createElementVNode("span", { class: "permission-request-drawer__header-container--description" }, "Ведение образовательной деятельности на территории ИНТЦ МГУ «Воробьевы горы»")
        ], -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_4, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createVNode(FormPrivacyConsentField, {
            "model-value": form.privacy,
            "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((form.privacy) = $event)),
            class: "permission-request-drawer__footer-policy"
          }, null, 8, ["model-value"]),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary btn-medium btn-cancel",
              onClick: onClose
            }, " Отмена "),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary btn-medium",
              onClick: onSubmit,
              disabled: isSubmitDisabled.value
            }, _toDisplayString(isSubmitting.value ? 'Отправка…' : 'Отправить'), 9, _hoisted_19)
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _cache[21] || (_cache[21] = _createElementVNode("span", { class: "permission-request-drawer__section-title" }, "1. Общее", -1)),
          _createVNode(_component_a_form, {
            ref_key: "generalFormRef",
            ref: generalFormRef,
            model: form,
            rules: rules,
            layout: "vertical",
            class: "permission-request-drawer__form"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Полное название юр. лица или ИП",
                  name: "fullLegalName",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.fullLegalName,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.fullLegalName) = $event)),
                      placeholder: "Полное название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Сокращенное название юр. лица или ИП",
                    name: "shortLegalName",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_input, {
                        value: form.shortLegalName,
                        "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.shortLegalName) = $event)),
                        placeholder: "Сокращенное название"
                      }, null, 8, ["value"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Организационно-правовая форма",
                    name: "organizationalLegalForm",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_input, {
                        value: form.organizationalLegalForm,
                        "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.organizationalLegalForm) = $event)),
                        placeholder: "ООО, АО и т.д."
                      }, null, 8, ["value"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Адрес местонахождения",
                  name: "locationAddress",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.locationAddress,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.locationAddress) = $event)),
                      placeholder: "Фактический адрес"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "ОГРН/ОГРНИП",
                    name: "ogrnOrOgrnip",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_input, {
                        value: form.ogrnOrOgrnip,
                        "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.ogrnOrOgrnip) = $event)),
                        placeholder: "Номер записи о создании юр. лица или ИП"
                      }, null, 8, ["value"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Дата регистрации",
                    name: "registrationDate",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_date_picker, {
                        value: form.registrationDate,
                        "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((form.registrationDate) = $event)),
                        class: "permission-request-drawer__date",
                        locale: _unref(pickerLocale),
                        format: "DD.MM.YYYY",
                        placeholder: "ДД.ММ.ГГГГ",
                        style: {"width":"100%"}
                      }, null, 8, ["value", "locale"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Наименование органа осуществляющего государственную регистрацию",
                  name: "registrationAuthority",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.registrationAuthority,
                      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((form.registrationAuthority) = $event)),
                      placeholder: "МВД, ФНС и т.д."
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Виды, уровни образования, планируемые к осуществлению на территории ИНТЦ",
                  name: "educationTypesAndLevels",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: form.educationTypesAndLevels,
                      "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((form.educationTypesAndLevels) = $event)),
                      mode: "multiple",
                      "allow-clear": "",
                      "show-arrow": "",
                      "option-label-prop": "label",
                      "popup-class-name": "permission-request-drawer__multiselect-dropdown",
                      class: "permission-request-drawer__select-multiple",
                      placeholder: "Не выбраны"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(educationTypesAndLevelsOptions.value, (opt) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: opt.value,
                            value: opt.value,
                            label: opt.label
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_9, [
                                _cache[20] || (_cache[20] = _createElementVNode("span", {
                                  class: "permission-request-drawer__multiselect-option-checkbox",
                                  "aria-hidden": "true"
                                }, null, -1)),
                                _createElementVNode("span", _hoisted_10, _toDisplayString(opt.label), 1)
                              ])
                            ]),
                            _: 2
                          }, 1032, ["value", "label"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Формы реализации образовательной деятельности на территории ИНТЦ",
                  name: "educationActivityFormats",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_checkbox_group, {
                      value: form.educationActivityFormats,
                      "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((form.educationActivityFormats) = $event)),
                      class: "permission-request-drawer__checkbox-group"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_11, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(educationActivityFormatOptions.value, (option) => {
                            return (_openBlock(), _createBlock(_component_a_checkbox, {
                              key: option.value,
                              value: option.value
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(option.label), 1)
                              ]),
                              _: 2
                            }, 1032, ["value"]))
                          }), 128))
                        ])
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _createVNode(_component_a_divider, { class: "permission-request-drawer__divider" }),
        _createElementVNode("div", _hoisted_12, [
          _cache[22] || (_cache[22] = _createElementVNode("span", { class: "permission-request-drawer__section-title" }, "1. Контактное лицо", -1)),
          _createVNode(_component_a_form, {
            ref_key: "contactFormRef",
            ref: contactFormRef,
            model: form,
            rules: rules,
            layout: "vertical",
            class: "permission-request-drawer__form"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_13, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "ФИО",
                  name: "contactFullName",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.contactFullName,
                      "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((form.contactFullName) = $event)),
                      placeholder: "Полное имя"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(FormPhoneField, {
                    "model-value": form.phone,
                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.phone) = $event))
                  }, null, 8, ["model-value"]),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Почта",
                    name: "contactEmail",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_input, {
                        value: form.contactEmail,
                        "onUpdate:value": _cache[11] || (_cache[11] = ($event: any) => ((form.contactEmail) = $event)),
                        placeholder: "example@mail.com"
                      }, null, 8, ["value"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Описание проекта",
                  name: "educationProgramDescription",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_textarea, {
                      value: form.educationProgramDescription,
                      "onUpdate:value": _cache[12] || (_cache[12] = ($event: any) => ((form.educationProgramDescription) = $event)),
                      class: "permission-request-drawer__textarea",
                      "auto-size": { minRows: 4, maxRows: 8 },
                      maxlength: EDUCATION_PROGRAM_DESCRIPTION_MAX_LENGTH,
                      placeholder: "Тип проекта, ожидаемые результаты",
                      "show-count": ""
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _createVNode(_component_a_divider, { class: "permission-request-drawer__divider" }),
        _createElementVNode("div", _hoisted_15, [
          _cache[23] || (_cache[23] = _createElementVNode("span", { class: "permission-request-drawer__section-title" }, "3. Документы", -1)),
          _createElementVNode("div", _hoisted_16, [
            _createVNode(FormFileUpload, {
              ref_key: "documentsUploadRef",
              ref: documentsUploadRef,
              modelValue: documentItems.value,
              "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((documentItems).value = $event))
            }, null, 8, ["modelValue"])
          ])
        ]),
        _createVNode(_component_a_divider, { class: "booking-infrastructure-drawer__mobile-divider" }),
        _createVNode(FormPrivacyConsentField, {
          "model-value": form.privacy,
          "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((form.privacy) = $event)),
          class: "booking-infrastructure-drawer__mobile-policy"
        }, null, 8, ["model-value"])
      ]),
      _: 1
    }, 8, ["open"])),
    _createVNode(ConsultationRequestSuccessModal, {
      open: openModal.value,
      "onUpdate:open": _cache[16] || (_cache[16] = ($event: any) => ((openModal).value = $event)),
      description: successModalDescription.value
    }, null, 8, ["open", "description"])
  ], 64))
}
}

})